import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { getLenis } from "@/motion/SmoothScroll";

import AboutMenu from "./AboutMenu";
import { logoFor } from "./content";

/**
 * The canvas's `Nav` screen.
 *
 * The remaining links are in-page anchors in the design, not routes, and they
 * stay that way here — "Pricing" pointing at `#contact` is the design's
 * decision, and quietly repointing it at /pricing would be a content change
 * wearing a refactor's clothes.
 *
 * About Us is the exception, and deliberately so: it is a real dropdown to four
 * real routes, restoring the menu the site header carries. See AboutMenu.tsx.
 *
 * Below 900px the link row becomes a stacked panel behind a toggle. The canvas
 * was drawn at desktop width and carried no breakpoint at all, so the row ran
 * past the right edge of a phone and was clipped — every page on this canvas,
 * not just one, could not be navigated on mobile.
 *
 * The links are not duplicated into a separate mobile menu. There is one set of
 * anchors in the document and `.dc-navlinks` in styles.ts only changes where
 * they are laid out. A second copy would be the obvious implementation and is
 * the wrong one twice over: it puts two "Solutions" anchors inside the <nav>,
 * which breaks the suite's scoped `getByRole("link")` queries, and it doubles
 * every destination for a screen reader walking the bar.
 */

interface NavProps {
  /** Which ground the bar is painted on, so the mark can pick its master. */
  dark: boolean;
  themeIcon: string;
  themeLabel: string;
  onToggleTheme: () => void;
}

/**
 * A nav entry is either an in-page anchor (`href`) or a real route (`to`).
 *
 * The canvas drew this as a single-page design, so every label pointed at a
 * section. That is right for Home, which really is the top of this page, and
 * wrong for anything the site has an actual page for: Solutions sending the
 * visitor to the homepage's capability strip means the Solutions page can never
 * be reached from the nav at all.
 */
type NavLinkSpec =
  | { label: string; href: string }
  | { label: string; to: string };

/**
 * Home always points at the homepage.
 *
 * The canvas used `#top`, which was right for a one-page design and wrong here
 * twice over: from another page that anchor resolves to the top of the page you
 * are already on, and even on the homepage it writes `#top` into the address
 * bar for what the visitor read as "go home". Both the wordmark and this entry
 * are plain links to `/`; returning to the top when already there is handled by
 * scrollToTop below rather than by a fragment.
 */
const HOME_LINK: NavLinkSpec = { label: "Home", to: "/" };

/**
 * Send the page back to the top.
 *
 * Routed through Lenis when it is running. Calling `window.scrollTo` while
 * Lenis owns the scroll sets the browser's position but not Lenis's internal
 * target, so its next frame animates the page straight back down to where the
 * visitor was.
 */
function scrollToTop() {
  const lenis = getLenis();
  if (lenis) {
    lenis.scrollTo(0);
    return;
  }
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, behavior: reduce ? "instant" : "smooth" });
}

const LINKS_AFTER: NavLinkSpec[] = [
  { label: "Solutions", to: "/solutions" },
  { label: "Pricing", to: "/pricing" },
  { label: "Contact Us", to: "/contact" },
];

const wordmarkStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: "var(--ink)",
  whiteSpace: "nowrap",
};

function WordmarkLink({
  pathname,
  children,
}: {
  pathname: string;
  children: React.ReactNode;
}) {
  const atHome = pathname === "/";
  return (
    <Link
      to="/"
      style={wordmarkStyle}
      // Already home: React Router will not re-navigate to the same path, so
      // nothing would happen. Scroll instead, and replace rather than push so
      // repeated clicks do not stack identical history entries.
      replace={atHome}
      onClick={() => {
        if (atHome) scrollToTop();
      }}
    >
      {children}
    </Link>
  );
}

/**
 * One nav entry, rendered as a router Link or a plain anchor depending on
 * whether it points at a route or a section. A route entry lights up when the
 * visitor is on it, and carries aria-current so that is not colour-only.
 */
function NavEntry({
  spec,
  pathname,
}: {
  spec: NavLinkSpec;
  pathname: string;
}) {
  const restingStyle = { color: "rgba(var(--ink-rgb),.78)" };

  if ("to" in spec) {
    // "/" would otherwise match every route via the startsWith test.
    const active =
      spec.to === "/"
        ? pathname === "/"
        : pathname === spec.to || pathname.startsWith(`${spec.to}/`);
    const atSelf = pathname === spec.to;
    return (
      <Link
        to={spec.to}
        aria-current={active ? "page" : undefined}
        className="dc-h-ink"
        style={active ? { color: "var(--ink)" } : restingStyle}
        replace={atSelf}
        onClick={() => {
          // Clicking the entry for the page you are on scrolls back up rather
          // than doing nothing, which is what a same-path navigation does.
          if (atSelf) scrollToTop();
        }}
      >
        {spec.label}
      </Link>
    );
  }

  return (
    <a href={spec.href} className="dc-h-ink" style={restingStyle}>
      {spec.label}
    </a>
  );
}

/**
 * The stacked-panel toggle. Drawn rather than set as a glyph, because the two
 * bars have to become an X and a character cannot be animated into one.
 */
function ToggleIcon({ open }: { open: boolean }) {
  const bar: React.CSSProperties = {
    transformBox: "fill-box",
    transformOrigin: "center",
    transition: "transform .22s cubic-bezier(.22,1,.36,1)",
  };
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <rect
        x="3"
        y="7"
        width="14"
        height="1.6"
        rx=".8"
        fill="currentColor"
        style={{
          ...bar,
          transform: open ? "translateY(2.2px) rotate(45deg)" : "none",
        }}
      />
      <rect
        x="3"
        y="11.4"
        width="14"
        height="1.6"
        rx=".8"
        fill="currentColor"
        style={{
          ...bar,
          transform: open ? "translateY(-2.2px) rotate(-45deg)" : "none",
        }}
      />
    </svg>
  );
}

export default function Nav({
  dark,
  themeIcon,
  themeLabel,
  onToggleTheme,
}: NavProps) {
  const { pathname } = useLocation();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const barRef = useRef<HTMLElement>(null);

  /**
   * Whether content is currently passing underneath the bar.
   *
   * This replaces a 26vw cyan highlight that swept the bottom edge on a 4.5s
   * linear loop. Two things were wrong with it, and the first is the one that
   * matters: a thin coloured bar travelling horizontally under the top chrome
   * is the universal indeterminate progress bar, so the nav read as permanently
   * loading. Things that look the same have to behave the same. It also ran
   * forever in the corner of the eye of every visitor on every page, at one
   * cycle per 4.5s — 0.22Hz, squarely inside the slow-oscillation band that
   * vestibular guidance says to avoid — while answering to nothing the visitor
   * did and indicating nothing about the page.
   *
   * What the edge does now is state indication, which is a purpose that can be
   * named: at the top of the page nothing is under the bar, so it carries no
   * divider at all and sits at its lightest. Once content scrolls beneath, the
   * material thickens and a soft edge fades in under it. Separation appears
   * only where floating chrome actually overlaps content.
   *
   * Observed rather than measured on scroll, so nothing runs on the main
   * thread between state changes.
   *
   * Watching the BAR itself is the usual trick — a sticky element's ratio drops
   * below 1 the moment it pins, given a -1px top rootMargin. It does not work
   * here and the reason is worth recording: that trick needs something above
   * the sticky element, and this bar is the first thing on the page. Sitting at
   * y=0 even when unpinned, it is clipped by the -1px margin from the first
   * frame, so it measured as stuck at the top of the page and the lighter state
   * never appeared at all.
   *
   * A sentinel in front of it has no such ambiguity: it is on screen at rest
   * and gone once the page moves. Eight pixels rather than one so a trackpad's
   * idle jitter cannot flicker the bar, and pulled back by its own height so it
   * occupies no space.
   */
  const [stuck, setStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Arriving somewhere is the end of using the menu. Without this the panel
  // stays open over the page the visitor just asked for.
  useEffect(() => setOpen(false), [pathname]);

  // Escape and an outside press, the two dismissals a panel that covers content
  // has to answer to. Bound only while open so a closed nav costs nothing.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!barRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  // The panel is `position: absolute` under a sticky bar, so it scrolls with
  // the page rather than staying put. Locking the body while it is open keeps
  // the panel and the bar together instead of letting the page slide behind it.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const closePanel = useCallback(() => setOpen(false), []);

  return (
    <>
      <div
        ref={sentinelRef}
        aria-hidden="true"
        style={{ height: 8, marginBottom: -8 }}
      />
      <nav
          ref={barRef}
          className="dc-nav"
          data-stuck={stuck ? "" : undefined}
          style={{ position: "sticky", top: 0, zIndex: 60 }}
        >
        <div
          style={{
            maxWidth: 1240,
            margin: "0 auto",
            // Narrower gutter on a phone: at 32px the wordmark, the two utilities
            // and the toggle no longer fit across a 360px viewport.
            padding: "0 clamp(18px, 4vw, 32px)",
            height: 68,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          {/* Same reasoning as the Home entry: a wordmark that only scrolls the
              current page to the top is not a way back to the homepage. */}
          <WordmarkLink pathname={pathname}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 17,
                letterSpacing: "-0.01em",
              }}
            >
              <img
                src={logoFor(dark)}
                alt="AI2Aim logo"
                style={{ width: 26, height: 26, display: "block" }}
              />{" "}
              AI2Aim
            </span>
            {/* Dropped below 900px: it is the first thing worth losing when the
                bar has to choose between a strapline and the destinations. */}
            {/* Centred on the wordmark by cap height, not by line box.
                The link is already `align-items: center`, but that centres the
                two line BOXES, and a 10px box and a 17px box carry different
                half-leading — so the painted caps landed with the strapline's
                baseline 2.63px above the wordmark's, which reads as floating
                rather than centred. `line-height: 1` collapses the leading so
                the box hugs the glyphs and the centring is deterministic instead
                of an accident of two font sizes. */}
            <span
              className="dc-navtag"
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 10,
                lineHeight: 1,
                letterSpacing: ".12em",
                textTransform: "uppercase",
                color: "rgba(var(--ink-rgb),.62)",
              }}
            >
              Aim. Command &amp; Execute.
            </span>
          </WordmarkLink>
          {/* The destinations. One set of anchors; `.dc-navlinks` lays them out as
              this row on a wide viewport and as a stacked panel on a narrow one. */}
          <div
            className="dc-navlinks"
            id={menuId}
            data-open={open}
            onClick={(e) => {
              // Choosing a destination ends the interaction. The route change
              // above already closes the panel, but picking the page you are
              // currently on does not change the path — and the panel would sit
              // there covering the answer. Scoped to anchors so that opening the
              // About Us submenu does not dismiss the panel containing it.
              if ((e.target as HTMLElement).closest("a")) closePanel();
            }}
          >
            <NavEntry spec={HOME_LINK} pathname={pathname} />
            <AboutMenu />
            {LINKS_AFTER.map((l) => (
              <NavEntry key={l.label} spec={l} pathname={pathname} />
            ))}
          </div>

          {/* The utilities, which stay in the bar at every width — the theme
              toggle and the demo link are both things a visitor reaches for
              without wanting to open a menu first. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "clamp(10px, 1.6vw, 18px)",
              fontSize: 13.5,
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            <button
              type="button"
              onClick={onToggleTheme}
              aria-label={themeLabel}
              title={themeLabel}
              className="dc-h-icon"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "1px solid rgba(var(--ink-rgb),.3)",
                background: "none",
                color: "var(--ink)",
                cursor: "pointer",
                fontSize: 15,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                transition: "border-color .2s, background .2s",
              }}
            >
              {themeIcon}
            </button>
            {/* Book a Demo goes to the contact page, not the closing band. */}
            <Link
              to="/contact"
              className="dc-h-btn-sm"
              style={{
                display: "inline-block",
                whiteSpace: "nowrap",
                background: "var(--btn)",
                color: "var(--btnfg)",
                padding: "10px clamp(14px, 3vw, 20px)",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 13,
                transition: "transform .2s, box-shadow .2s",
              }}
            >
              Book a Demo
            </Link>

            <button
              type="button"
              className="dc-navtoggle dc-h-icon"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={menuId}
              aria-label={open ? "Close menu" : "Open menu"}
              // No inline `display`: an inline value outranks the class rule, and
              // `.dc-navtoggle` is what decides whether this button exists at the
              // current width. Setting it here showed the toggle on desktop.
              style={{
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                padding: 0,
                borderRadius: "50%",
                border: "1px solid rgba(var(--ink-rgb),.3)",
                background: "none",
                color: "var(--ink)",
                cursor: "pointer",
                transition: "border-color .2s, background .2s",
              }}
            >
              <ToggleIcon open={open} />
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}
